import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { AccountTreeOutlined, ContentCopyOutlined, LinkOffOutlined, RateReviewOutlined, ScheduleOutlined, SyncOutlined, NetworkCheckOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { LoadingPage } from '../../../components/pages/loading-page';
import { InformationBox } from '../../../components/presentation-components/miscellaneous-components/information-box';
import { ShallowFormDisplay } from '../../../components/presentation-components/form-field-components/general/shallow-form-display';
import { FormTextInputDisplay } from '../../../components/presentation-components/form-field-components/new-and-edit/form-text-input';
import { FormSelectInputDisplay } from '../../../components/presentation-components/form-field-components/new-and-edit/form-select-input';
import { TableDisplay } from '../../../components/presentation-components/miscellaneous-components/table-display';
import { ScheduleSyncDialog } from '../../../components/dialogs/settings/schedule-sync-dialog';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import BusinessCentralService from '../shared-services/business-central/business-central.service';
import { IBusinessCentralCompany, IBusinessCentralStatus, IBusinessCentralSync } from '../shared-services/business-central/business-central.model';
import { BusinessCentralConnectPanel } from './business-central-connect-panel';
import { BusinessCentralAppSteps, businessCentralAppStepsText } from './business-central-app-steps';
import { TEST_STATUS_LABELS, connectionChip, describeSync, formatDateTime, syncChip } from './business-central-format';
import ThirdPartyConnectionDetailsService from '../shared-services/third-party-connection-details/third-party-connection.service';
import { buildCoverageMessage } from '../../../shared/utils/coverage-summary-utils';

const cardSx = { borderColor: 'lightGrey.main', borderRadius: '10px', boxShadow: 'none' };
// Where Business Central records are reviewed: the Financial page, filtered to this integration.
const REVIEW_LINK = '/emissions/financial?tabId=0&integration=businesscentral';

/**
 * The details page for Business Central, composed like the cloud connectors' page
 * (CloudEmissionsDetails): status rows, the connect panel until a tenant is linked, then the
 * companies with their latest sync, Sync now, the schedule, the chart of accounts and disconnect.
 * It replaces the ledger connectors' generic Details, which is built around a user's OAuth sign-in
 * that Business Central does not have. A new composition rather than a generalised Details: no
 * existing details page fits, and it reuses only shared building blocks. Syncing itself is the
 * shared ledger sync, and the records are reviewed on the Financial page with every other
 * integration's.
 */
export const BusinessCentralDetails: FunctionComponent = () => {
  const businessCentralService = useMemo(() => new BusinessCentralService(), []);
  const thirdPartyConnectionDetailsService = useMemo(() => new ThirdPartyConnectionDetailsService(), []);
  const { userDetails } = useContext(UserDetailsContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const reportPeriodContext = useContext(ReportPeriodContext);
  const isAdmin = userDetails?.roles?.includes('admin') ?? false;

  const [status, setStatus] = useState<IBusinessCentralStatus | null>(null);
  const [pageReady, setPageReady] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [syncingCompanyId, setSyncingCompanyId] = useState<string | null>(null);
  const [openSchedule, setOpenSchedule] = useState(false);
  const [chartCompanyId, setChartCompanyId] = useState('');
  const [editField, setEditField] = useState('');
  const [environment, setEnvironment] = useState('');
  const [isSavingEnvironment, setIsSavingEnvironment] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedSyncs, setSelectedSyncs] = useState<string[]>([]);
  const navigate = useNavigate();

  // Memoised: the imports table's rows read it, and TableDisplay needs rows that stay put.
  const periods = useMemo(() => reportPeriodContext.allReportPeriods || [], [reportPeriodContext.allReportPeriods]);
  const [reportPeriodId, setReportPeriodId] = useState<string>(reportPeriodContext.lastSelectedOpenReportId || reportPeriodContext.lastSelectedReportId || '');
  const selectedPeriod = periods.find(period => period.id === reportPeriodId);

  const savedEnvironment = status?.connection?.environmentName ?? status?.defaultEnvironment ?? 'Production';

  const loadStatus = () => businessCentralService.getStatus()
    .then(response => {
      setStatus(response);
      setErrorText('');
    })
    .catch(err => {
      console.error('Error loading Business Central:', err);
      setErrorText('This integration could not be loaded. Please try again.');
    })
    .finally(() => setPageReady(true));

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!reportPeriodId && periods.length) setReportPeriodId(reportPeriodContext.lastSelectedOpenReportId || periods[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods.length]);

  useEffect(() => {
    if (editField !== 'environmentName') setEnvironment(savedEnvironment);
  }, [savedEnvironment, editField]);

  // The consent and sign-in happen in another tab; coming back to this one picks the result up.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncByCompany = useMemo(
    () => new Map((status?.syncs || []).map(sync => [sync.companyId, sync])),
    [status],
  );

  const errorSnackbar = (err: any, fallback: string) => userNotificationsContext.openSnackbar({
    open: true,
    message: err?.response?.data?.message || fallback,
    backgroundColor: 'text.primary',
  });

  const testConnection = () => {
    setIsTesting(true);
    businessCentralService.testConnection()
      .then(response => {
        setStatus(response);
        const result = response.testResult;
        userNotificationsContext.openSnackbar({
          open: true,
          message: result?.status === 'connected'
            ? `Business Central can be read. ${result.companyCount} ${result.companyCount === 1 ? 'company is' : 'companies are'} visible.`
            : (result?.message || 'Business Central could not be reached.'),
          backgroundColor: result?.status === 'connected' ? 'primary.dark' : 'text.primary',
        });
      })
      .catch(err => errorSnackbar(err, 'The connection could not be tested. Please try again.'))
      .finally(() => setIsTesting(false));
  };

  const saveEnvironment = () => {
    const name = environment.trim();
    if (!name || name === savedEnvironment) {
      setEditField('');
      return;
    }
    setIsSavingEnvironment(true);
    businessCentralService.updateEnvironment(name)
      .then(response => {
        setStatus(response);
        setEditField('');
        userNotificationsContext.openSnackbar({ open: true, message: 'Environment saved', backgroundColor: 'primary.dark' });
      })
      .catch(err => errorSnackbar(err, 'The environment could not be saved. Please try again.'))
      .finally(() => setIsSavingEnvironment(false));
  };

  // The shared ledger sync, as the Financial page's Sync Data runs it: it reads the company's
  // posted invoices for the period and the records wait under Pending via Integrations.
  const syncNow = (company: IBusinessCentralCompany) => {
    if (!reportPeriodId) return;
    setSyncingCompanyId(company.companyId);
    thirdPartyConnectionDetailsService.getTransactions(reportPeriodId, 'businesscentral', `?companyId=${encodeURIComponent(company.companyId)}`)
      .then(response => {
        const coverage = response?.coverageSummary || null;
        const imported = (response?.thirdPartyConnectionImportLines || []).filter((line: any) => !line.isDiscarded).length;
        userNotificationsContext.openSnackbar({
          open: true,
          message: `${coverage ? buildCoverageMessage(coverage) : `${imported} new record${imported === 1 ? '' : 's'} imported.`} Review them under Pending via Integrations on the Financial page.`,
          backgroundColor: 'primary.dark',
        });
        loadStatus();
      })
      .catch(err => errorSnackbar(err, 'The sync could not be completed. Please try again.'))
      .finally(() => setSyncingCompanyId(null));
  };

  const disconnect = async () => {
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: 'Disconnect?',
      message: 'Invoice lines already imported, and emissions already committed, stay. Nothing new is read until you connect again.',
      buttonCopy: 'Disconnect',
      cancelButtonCopy: 'Cancel',
    });
    if (!confirmed) return;
    businessCentralService.disconnect()
      .then(response => {
        userNotificationsContext.openSnackbar({ open: true, message: response.message, backgroundColor: 'text.primary' });
        loadStatus();
      })
      .catch(err => errorSnackbar(err, 'The connection could not be removed. Please try again.'));
  };

  // Every company Business Central lists, plus any that has synced before but is not listed now
  // (removed, or the list could not be read), so its last sync stays visible.
  const companies: IBusinessCentralCompany[] = useMemo(() => {
    const listed = status?.companies || [];
    const listedIds = new Set(listed.map(company => company.companyId));
    const unlisted = (status?.syncs || [])
      .filter(sync => !listedIds.has(sync.companyId))
      .map(sync => ({ companyId: sync.companyId, companyName: sync.companyName || sync.companyId }));
    return [...listed, ...unlisted];
  }, [status]);

  useEffect(() => {
    if (!chartCompanyId && status?.companies?.length) setChartCompanyId(status.companies[0].companyId);
  }, [status, chartCompanyId]);

  const headCells: IHeadCell[] = [
    { id: 'company', label: 'Company', numeric: false, sortable: true },
    { id: 'state', label: 'Last sync', numeric: false, sortable: false },
    { id: 'detail', label: 'Details', numeric: false, sortable: false },
    { id: 'waiting', label: 'Waiting for review', numeric: true, sortable: true },
    { id: 'action', label: '', numeric: false, sortable: false },
  ];

  // TableDisplay copies its rows into state in an effect keyed on the array, so the rows must be
  // memoised or every render would hand it a new array and it would never settle. The Sync now
  // handler is read through a ref so the memo does not depend on a function rebuilt every render.
  const syncNowRef = useRef(syncNow);
  syncNowRef.current = syncNow;
  const periodLocked = !!selectedPeriod?.locked;
  const rows = useMemo(() => companies.map(company => {
    const sync: IBusinessCentralSync | undefined = syncByCompany.get(company.companyId);
    const chip = syncChip(sync);
    const listed = (status?.companies || []).some(listedCompany => listedCompany.companyId === company.companyId);
    return {
      id: company.companyId,
      company: company.companyName,
      state: <Chip label={chip.label} color={chip.color} variant='outlined' size='small' />,
      detail: sync ? (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>{describeSync(sync)}</Typography>
      ) : '',
      waiting: sync ? sync.waitingForReview : 0,
      action: listed ? (
        <LoadingButton
          variant='outlined'
          size='small'
          loading={syncingCompanyId === company.companyId}
          disabled={!reportPeriodId || periodLocked}
          startIcon={<SyncOutlined fontSize='small' />}
          onClick={(event) => { event.stopPropagation(); syncNowRef.current(company); }}
        >
          <Typography sx={{ pl: 0.5 }}>Sync now</Typography>
        </LoadingButton>
      ) : '',
    };
  }), [companies, syncByCompany, status, syncingCompanyId, reportPeriodId, periodLocked]);

  // Every sync, newest first. The ones with records still waiting open the Financial page,
  // where Business Central records are reviewed like any other integration's.
  const recentSyncs = useMemo(() => status?.recentSyncs || [], [status]);
  const needsReviewCount = recentSyncs.filter(sync => sync.waitingForReview > 0).length;
  const syncHeadCells: IHeadCell[] = [
    { id: 'synced', label: 'Synced', numeric: false, sortable: true, sortValue: 'syncedAt' },
    { id: 'company', label: 'Company', numeric: false, sortable: true },
    { id: 'period', label: 'Report period', numeric: false, sortable: true },
    { id: 'fetched', label: 'Read', numeric: true, sortable: true },
    { id: 'imported', label: 'Imported', numeric: true, sortable: true },
    { id: 'waiting', label: 'Waiting for review', numeric: true, sortable: true },
    { id: 'action', label: '', numeric: false, sortable: false },
  ];
  const syncRows = useMemo(() => recentSyncs.map(sync => {
    const period = periods.find(candidate => candidate.id === sync.reportPeriodId);
    return {
      id: sync.importId,
      synced: formatDateTime(sync.syncedAt),
      syncedAt: sync.syncedAt,
      company: sync.companyName || sync.companyId,
      period: period ? (period.friendlyName || String(period.calendarYear)) : '',
      fetched: sync.fetchedCount,
      imported: sync.importedCount,
      waiting: sync.waitingForReview,
      action: sync.waitingForReview > 0 ? (
        <Button variant='outlined' size='small' component={RouterLink} to={REVIEW_LINK} startIcon={<RateReviewOutlined fontSize='small' />}>
          <Typography sx={{ pl: 0.5 }}>Review</Typography>
        </Button>
      ) : '',
    };
  }), [recentSyncs, periods]);

  const lastTest = status?.connection?.lastTest;
  const chip = status ? connectionChip(status) : null;
  // Microsoft access is granted and the tenant linked, but Business Central has not let the
  // application in: the step people get stuck on, so the page lists it rather than just naming it.
  const needsBusinessCentralStep = !!status?.connected && lastTest?.status === 'business-central-step';

  // Mapping happens on the Chart of Accounts page, which reads the chart from Business Central and
  // lists it on every visit after that. A tenant with several companies can keep a different chart
  // in each, so the company chosen below travels with the link.
  const chartOfAccountsLink = `/settings/data-upload/chart-of-accounts${(status?.companies || []).length > 1 && chartCompanyId ? `?companyId=${encodeURIComponent(chartCompanyId)}` : ''}`;

  const copyAppSteps = () => {
    navigator.clipboard.writeText(businessCentralAppStepsText(status?.clientId ?? '', status?.entraApplicationsUrl))
      .then(() => userNotificationsContext.openSnackbar({ open: true, message: 'Steps copied. Send them to whoever administers Business Central.', backgroundColor: 'text.primary' }))
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'Could not copy; select the text by hand', backgroundColor: 'text.primary' }));
  };

  return (
    <LoadingPage pageReady={pageReady}>
      <>
        {errorText && <InformationBox icon={IconName.ErrorOutline} text={errorText} />}
        {status && !status.configured && (
          <InformationBox
            icon={IconName.ErrorOutline}
            text='This integration is not available yet. Contact support to have it switched on for your account.'
          />
        )}
        {status && status.configured && chip && (
          <Stack spacing={3}>
            <Box component='form' onSubmit={(event: any) => { event.preventDefault(); saveEnvironment(); }}>
              <ShallowFormDisplay name='Connection' chip={chip} isTopRow />
              {status.connected && status.connection?.tenantId && (
                <ShallowFormDisplay
                  name='Microsoft tenant'
                  value={`${status.connection.tenantId}${status.connection.verifiedDomain ? `, linked by a user at ${status.connection.verifiedDomain}` : ''}${status.connection.verifiedAt ? ` on ${formatDateTime(status.connection.verifiedAt)}` : ''}`}
                />
              )}
              <FormTextInputDisplay
                isEditPage
                allowEdit={isAdmin}
                editField={editField}
                setEditField={setEditField}
                disabled={editField !== 'environmentName' || isSavingEnvironment}
                title='Environment'
                name='environmentName'
                value={environment}
                onChange={(event: any) => setEnvironment(event.target.value)}
                cancelEditHandler={() => { setEnvironment(savedEnvironment); setEditField(''); }}
                tooltip='The Business Central environment to read from, exactly as Business Central names it. Usually Production.'
                isBottomRow={!status.connected}
              />
              {status.connected && (
                <ShallowFormDisplay
                  name='Last check'
                  value={lastTest
                    ? `${formatDateTime(lastTest.at)}: ${TEST_STATUS_LABELS[lastTest.status]}${lastTest.status === 'connected' ? ` (${lastTest.companyCount} ${lastTest.companyCount === 1 ? 'company' : 'companies'})` : ''}`
                    : 'Not checked yet'}
                  isBottomRow
                />
              )}
            </Box>

            {status.connected && lastTest && lastTest.status !== 'connected' && lastTest.message && !needsBusinessCentralStep && (
              <Alert severity='warning'>{lastTest.message}</Alert>
            )}

            {needsBusinessCentralStep && (
              <Card variant='outlined' sx={cardSx}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack spacing={1}>
                      <Typography variant='h6' sx={{ color: 'text.primary' }}>One more step: allow the application in Business Central</Typography>
                      <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                        Access is granted in Microsoft and your tenant is linked, but Business Central keeps its own list
                        of what each application may read, and ours is not on it yet. A Business Central administrator
                        does this once. If that isn&apos;t you, copy the steps and send them over.
                      </Typography>
                    </Stack>
                    {lastTest?.message && <Alert severity='warning'>{lastTest.message}</Alert>}
                    <BusinessCentralAppSteps clientId={status.clientId ?? ''} entraApplicationsUrl={status.entraApplicationsUrl} />
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      Then test the connection. It can take a minute for Business Central to pick up a new record.
                    </Typography>
                    <Stack direction='row' flexWrap='wrap' useFlexGap gap={2} alignItems='center'>
                      <LoadingButton variant='contained' loading={isTesting} startIcon={<NetworkCheckOutlined fontSize='small' />} onClick={testConnection}>
                        <Typography sx={{ pl: 0.5 }}>Test connection</Typography>
                      </LoadingButton>
                      <Button variant='text' startIcon={<ContentCopyOutlined fontSize='small' />} onClick={copyAppSteps}>
                        <Typography sx={{ pl: 0.5 }}>Copy steps</Typography>
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
            {status.connected && status.companiesError && (!lastTest || lastTest.status === 'connected') && (
              <Alert severity='warning'>{status.companiesError}</Alert>
            )}

            {!status.connected && (
              <BusinessCentralConnectPanel status={status} isAdmin={isAdmin} onUpdated={setStatus} />
            )}

            {status.connected && (
              <Card variant='outlined' sx={cardSx}>
                <CardContent>
                  <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', md: 'center' }} gap={2}>
                      <Stack spacing={1}>
                        <Typography variant='h6' sx={{ color: 'text.primary' }}>Companies</Typography>
                        <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                          A sync reads the posted purchase invoices and credit memos dated in the report period
                          below. New records wait for your review before they count, under Pending via
                          Integrations on the <RouterLink to={REVIEW_LINK}>Financial page</RouterLink>.
                        </Typography>
                      </Stack>
                      {/* Flex gap rather than `spacing`, so a wrapped button keeps its gap. */}
                      <Stack direction='row' flexWrap='wrap' useFlexGap gap={2} alignItems='center'>
                        {isAdmin && (
                          <Button variant='outlined' startIcon={<ScheduleOutlined fontSize='small' />} onClick={() => setOpenSchedule(true)}>
                            <Typography sx={{ pl: 0.5 }}>Schedule sync</Typography>
                          </Button>
                        )}
                        {/* While the Business Central step is missing, its card above carries the test. */}
                        {!needsBusinessCentralStep && (
                          <LoadingButton variant='outlined' loading={isTesting} startIcon={<NetworkCheckOutlined fontSize='small' />} onClick={testConnection}>
                            <Typography sx={{ pl: 0.5 }}>Test connection</Typography>
                          </LoadingButton>
                        )}
                      </Stack>
                    </Stack>

                    <FormSelectInputDisplay
                      isEditPage={false}
                      allowEdit={false}
                      disabled={!!syncingCompanyId}
                      isTopRow
                      isBottomRow
                      title='Report period'
                      name='business-central-report-period'
                      value={reportPeriodId}
                      array={periods}
                      valueKey='id'
                      labelKey={(period: any) => `${period.friendlyName || period.calendarYear}${period.locked ? ' (locked)' : ''}`}
                      onChange={(event: any) => setReportPeriodId(event.target.value)}
                    />

                    {companies.length === 0 ? (
                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        {status.companiesError
                          ? 'The companies could not be read from Business Central.'
                          : 'No companies are visible yet. Check the Microsoft Entra Application record in Business Central has permission sets for each company, then test the connection.'}
                      </Typography>
                    ) : (
                      <TableDisplay
                        headCells={headCells}
                        rows={rows}
                        selected={selected}
                        setSelected={setSelected}
                        perPage={10}
                        defaultOrderBy='company'
                        defaultOrder='asc'
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {recentSyncs.length > 0 && (
              <Card variant='outlined' sx={cardSx}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack spacing={1}>
                      <Typography variant='h6' sx={{ color: 'text.primary' }}>Syncs</Typography>
                      <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                        {needsReviewCount > 0
                          ? `${needsReviewCount} ${needsReviewCount === 1 ? 'sync has' : 'syncs have'} records waiting for review. They don't count until they are committed. `
                          : 'Every synced record has been reviewed. '}
                        Review them under Pending via Integrations on the{' '}
                        <RouterLink to={REVIEW_LINK}>Financial page</RouterLink>, filtered to Business Central.
                      </Typography>
                    </Stack>
                    <TableDisplay
                      headCells={syncHeadCells}
                      rows={syncRows}
                      selected={selectedSyncs}
                      setSelected={setSelectedSyncs}
                      perPage={10}
                      defaultOrderBy='syncedAt'
                      defaultOrder='desc'
                    />
                  </Stack>
                </CardContent>
              </Card>
            )}

            {status.connected && (
              <Card variant='outlined' sx={cardSx}>
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', md: 'center' }} gap={2}>
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Typography variant='h6' sx={{ color: 'text.primary' }}>Chart of Accounts</Typography>
                      <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '640px' }}>
                        Give each account code an outcome once and every sync classifies itself, instead of
                        relying on supplier names. Codes left without an outcome keep classifying by supplier,
                        and records already waiting for review take a new outcome as soon as you save it.
                      </Typography>
                      {(status.companies || []).length > 1 && (
                        <Box sx={{ maxWidth: '640px', pt: 1 }}>
                          <FormSelectInputDisplay
                            isEditPage={false}
                            allowEdit={false}
                            disabled={false}
                            isTopRow
                            isBottomRow
                            title='Read the chart of'
                            name='business-central-chart-company'
                            value={chartCompanyId}
                            array={status.companies}
                            valueKey='companyId'
                            labelKey='companyName'
                            onChange={(event: any) => setChartCompanyId(event.target.value)}
                          />
                        </Box>
                      )}
                    </Stack>
                    <Button
                      variant='outlined'
                      component={RouterLink}
                      to={chartOfAccountsLink}
                      startIcon={<AccountTreeOutlined fontSize='small' />}>
                      <Typography sx={{ pl: 0.5 }}>Map account codes</Typography>
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {isAdmin && (status.connected || !!status.connection) && (
              <Stack direction='row' justifyContent='flex-end'>
                <Button variant='outlined' startIcon={<LinkOffOutlined fontSize='small' />} onClick={disconnect}>
                  <Typography sx={{ pl: 0.5 }}>Disconnect</Typography>
                </Button>
              </Stack>
            )}
          </Stack>
        )}

        {status?.connected && isAdmin && (
          <ScheduleSyncDialog
            open={openSchedule}
            onClose={() => setOpenSchedule(false)}
            provider='businesscentral'
            providerName='Dynamics 365 Business Central'
          />
        )}
      </>
    </LoadingPage>
  );
};
